import { useEffect, useMemo, useRef, useState } from "react"
import {
  agentComposerDraftKey,
  readAgentComposerDraft,
  writeAgentComposerDraft,
  type AgentComposerDraftKeyParts,
} from "@/lib/agent-composer-draft"

const PERSIST_DEBOUNCE_MS = 200

/** Restore / persist the assistant composer text for the current conversation. */
export function useAgentComposerDraft({ projectId, mode, contextType, contextId }: AgentComposerDraftKeyParts) {
  const key = useMemo(
    () => agentComposerDraftKey({ projectId, mode, contextType, contextId }),
    [projectId, mode, contextType, contextId],
  )
  const [input, setInput] = useState(() => readAgentComposerDraft(key))
  const keyRef = useRef(key)
  const inputRef = useRef(input)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  inputRef.current = input

  useEffect(() => {
    const cancelTimer = () => {
      if (timerRef.current == null) return
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (keyRef.current !== key) {
      cancelTimer()
      writeAgentComposerDraft(keyRef.current, inputRef.current)
      keyRef.current = key
      const next = readAgentComposerDraft(key)
      inputRef.current = next
      setInput(next)
      return
    }

    if (input === "") {
      cancelTimer()
      writeAgentComposerDraft(key, "")
      return
    }

    cancelTimer()
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      writeAgentComposerDraft(key, input)
    }, PERSIST_DEBOUNCE_MS)

    return cancelTimer
  }, [key, input])

  useEffect(() => {
    return () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      writeAgentComposerDraft(keyRef.current, inputRef.current)
    }
  }, [])

  return { input, setInput }
}
